import axios from "axios";
import { useCallback } from "react";
import {
  handleAuthRetry,
  isHandledLinkedAccountRefusal,
  requestLogin,
  useAPIHelper as fetchAPIData
} from "@/services/api/hook";
import { useAuth } from "@/services/auth/AuthContext";

export type ApiKeyConnectResult =
  | "connected"
  | "rejected"
  | "unavailable"
  | "failed";

// Sent outside react-query on purpose: a mutation keeps its variables in the
// mutation cache, and the key must not outlive the request in the browser.
export const useConnectWithApiKey = (endpoint: string) => {
  const { token, tenantId, tokenExpiry, login } = useAuth();

  return useCallback(
    async (apiKey: string): Promise<ApiKeyConnectResult> => {
      try {
        await fetchAPIData({ token, tenantId, tokenExpiry }, [
          endpoint,
          { method: "POST", data: { apiKey } }
        ]);
        return "connected";
      } catch (error) {
        // Reduce the failure to a status here: the request error holds the key in its config
        if (handleAuthRetry(0, error)) {
          // A refusal in a linked account is handled by switching back home, not by a new login
          if (!isHandledLinkedAccountRefusal(error)) {
            requestLogin(login);
          }
          return "failed";
        }
        const status = axios.isAxiosError(error)
          ? error.response?.status
          : undefined;
        if (status === 400) return "rejected";
        if (status === 503) return "unavailable";
        return "failed";
      }
    },
    [endpoint, token, tenantId, tokenExpiry, login]
  );
};
